import { TeamName } from "@/components/TeamName";
import { currencyFormatter } from "@/utils/currencyFormatter";
import {
  type InstallationQuotationStatus,
  type InstallationQuotationWidgetList,
} from "@repo/api";
import {
  Button,
  ListView,
  ListViewItem,
  Text,
  calendarIcon,
  personIcon,
  warehouseIcon,
} from "@repo/skapa";
import { getLanguageLocal, t, useTranslation } from "@repo/translation";
import {
  CoWorkerName,
  LabelField,
  ModalSheet,
  SectionContainer,
  TwoColumnSectionContainer,
  localizeDateAndTime,
  translateInstallationQuotationStatus,
  useSettings,
} from "@repo/ui";
import { useNavigate } from "react-router-dom";

interface ViewInstallationQuotationModalProps {
  isOpen: boolean;
  closeModal: () => void;
  installationQuotation?: InstallationQuotationWidgetList;
}

export const ViewInstallationQuotationModal = ({
  isOpen,
  closeModal,
  installationQuotation,
}: ViewInstallationQuotationModalProps) => {
  const navigate = useNavigate();
  const { market, language, general } = useSettings();
  const { dateFnsLocale } = useTranslation();

  if (!installationQuotation) {
    return null;
  }
  return (
    <ModalSheet
      title={`${t("label.installation_quotation")} - ${installationQuotation?.installationQuotationId}`}
      visible={isOpen}
      onClose={closeModal}
      footer={
        <Button
          type="primary"
          text={t("action.open_project")}
          onClick={() =>
            navigate(`/opportunity/${installationQuotation?.projectId}`)
          }
        />
      }
    >
      <SectionContainer>
        {installationQuotation?.projectName && (
          <Text textStyle="Heading.M">
            {installationQuotation?.projectName}
          </Text>
        )}
        <ListView id="installation-quotation-details" showDivider={false}>
          <ListViewItem
            title={t("label.owner")}
            description={
              installationQuotation.projectAssignedTo ? (
                <CoWorkerName email={installationQuotation.projectAssignedTo} />
              ) : (
                <TeamName
                  teamId={Number(installationQuotation.projectAssignedTeam)}
                />
              )
            }
            leadingIcon={personIcon}
          />
          <ListViewItem
            leadingIcon={warehouseIcon}
            title={t("label.cmp")}
            description={
              installationQuotation.buName ??
              installationQuotation.buCode ??
              "-"
            }
          />
          <ListViewItem
            title={t("label.customer")}
            description={
              installationQuotation.customerFirstName ||
              installationQuotation.customerLastName
                ? `${installationQuotation.customerFirstName ?? ""} ${installationQuotation.customerLastName ?? ""}`
                : "-"
            }
            leadingIcon={personIcon}
          />
          <ListViewItem
            leadingIcon={calendarIcon}
            title={t("label.installation_start_date")}
            description={localizeDateAndTime(
              installationQuotation.installationStartDate ?? "",
              dateFnsLocale
            )}
          />
          <ListViewItem
            leadingIcon={calendarIcon}
            title={t("label.installation_end_date")}
            description={localizeDateAndTime(
              installationQuotation.installationEndDate ?? "",
              dateFnsLocale
            )}
          />

          <ListViewItem
            leadingIcon={calendarIcon}
            title={t("label.docusign_date_sent")}
            description={localizeDateAndTime(
              installationQuotation.docusignDateSent ?? "",
              dateFnsLocale
            )}
          />
        </ListView>
        <TwoColumnSectionContainer>
          <LabelField
            id="id"
            label={t("label.quote_external_id")}
            value={installationQuotation.quoteExternalId ?? "-"}
          />
          <LabelField
            id="id"
            label={t("label.opportunity_external_id")}
            value={installationQuotation.opportunityExternalId ?? "-"}
          />
        </TwoColumnSectionContainer>
        <TwoColumnSectionContainer>
          <LabelField
            id="id"
            label={t("label.deposit_paid")}
            value={
              installationQuotation.depositPaid ? t("label.yes") : t("label.no")
            }
          />
          <LabelField
            id="id"
            label={t("label.grand_total")}
            value={
              currencyFormatter({
                value: installationQuotation.grandTotal ?? 0,
                currency: general?.currency.currency_code,
                locale: getLanguageLocal(language, market),
                showSign: true,
                disableDecimals: Boolean(
                  general?.localization?.disable_decimals
                ),
                fractions: 2,
              }) ?? "-"
            }
          />
        </TwoColumnSectionContainer>
        <TwoColumnSectionContainer>
          <LabelField
            id="id"
            label={t("label.quote_number")}
            value={installationQuotation.quoteNumber ?? "-"}
          />
          <LabelField
            id="id"
            label={t("label.status")}
            value={
              translateInstallationQuotationStatus(
                installationQuotation.status as InstallationQuotationStatus
              ) ?? "-"
            }
          />
        </TwoColumnSectionContainer>
      </SectionContainer>
    </ModalSheet>
  );
};
